import {
    DiscontinueReasons,
    UnmanageReasons,
    UpdateProductTypeReasons,
    UpdateSupplierReasons,
} from '@strata/constants';
import { IssueDetailList, type IssueType } from '@strata/react-status-icons';
import { type AdditionalContentReferralMilestone, ReferralTimeline } from '@strata/react-timeline';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo } from 'react';

import { useAppSelector } from 'store';
import { useGetReferralMilestonesQuery } from 'store/api/clientApi';
import {
    type SupplierMap,
    useGetSegmentsQuery,
    useGetSupplierMapQuery,
} from 'store/api/referenceApi';

/** Display text to mask FMH users in timeline */
const FMH_PROGRAM_SUPPORT = 'FMH Program Support';

/** Returns supplier display info for milestones */
const getSupplierInfoForMilestone = (
    supplierNpi: string | undefined,
    supplierMap: SupplierMap | undefined,
) => {
    const supplier = supplierMap?.[supplierNpi ?? ''];
    return {
        supplier_name: supplier?.name,
        network_status: supplier?.networkStatus,
    };
};

/** Mask free text reason values with 'Other' */
const maskNonStandardReasons = (comment: string, reasons: string[]) =>
    reasons.includes(comment) ? comment : 'Other';

export const useEnrichedReferralMilestones = (referralId: string) => {
    const { showOpportunityDetailsInPayersApp } = useFlags<{
        showOpportunityDetailsInPayersApp: boolean;
    }>();
    const { payerId } = useAppSelector((state) => state.auth);

    const { data: supplierMap } = useGetSupplierMapQuery();
    const { data: segments } = useGetSegmentsQuery({ payerId: payerId! });
    const { data: referralMilestones, isFetching: referralMilestonesFetching } =
        useGetReferralMilestonesQuery({
            payerId: payerId!,
            referralId,
        });

    const enrichedMilestones = useMemo<AdditionalContentReferralMilestone[] | undefined>(
        () =>
            referralMilestones
                // eslint-disable-next-line complexity
                ?.map((milestone) => {
                    if (
                        milestone.milestone_type ===
                        ReferralTimeline.MilestoneTypes.REFERRAL_STATUS_CHANGE
                    ) {
                        if (milestone.title === 'Active') {
                            return {
                                ...milestone,
                                additional_data: {
                                    // get supplier name & network status for ACTIVE milestone
                                    ...getSupplierInfoForMilestone(
                                        milestone.additional_data?.supplier_npi,
                                        supplierMap,
                                    ),
                                },
                            };
                        }
                        return {
                            ...milestone,
                            additional_data: undefined,
                        };
                    }
                    if (
                        milestone.milestone_type ===
                        ReferralTimeline.MilestoneTypes.POST_LIVE_REFERRAL_ASSIGNMENT
                    ) {
                        return {
                            ...milestone,
                            additional_data: {
                                ...milestone.additional_data,
                                // get supplier name & network status for POST LIVE REFERRAL ASSIGNMENT milestone
                                ...getSupplierInfoForMilestone(
                                    milestone.additional_data?.supplier_npi,
                                    supplierMap,
                                ),
                                // remove backstop_reason for POST LIVE REFERRAL ASSIGNMENT milestone
                                backstop_reason: undefined,
                            },
                        };
                    }
                    if (milestone.milestone_type === ReferralTimeline.MilestoneTypes.ROUTING) {
                        return {
                            ...milestone,
                            // Remove route_code from ROUTING milestone
                            comment: undefined,
                        };
                    }
                    if (
                        milestone.milestone_type ===
                        ReferralTimeline.MilestoneTypes.REFERRAL_OPPORTUNITY_INVALIDATED
                    ) {
                        return showOpportunityDetailsInPayersApp
                            ? {
                                  ...milestone,
                                  title: 'Received',
                                  additional_data: {
                                      ...milestone.additional_data,
                                      issues: IssueDetailList({
                                          issues: Object.keys(
                                              milestone.additional_data?.issues ?? {},
                                          ) as IssueType[],
                                      }),
                                      disposition: undefined,
                                      reject_code: undefined,
                                      outcome: undefined,
                                  },
                              }
                            : undefined;
                    }
                    if (milestone.milestone_type === ReferralTimeline.MilestoneTypes.DISCONTINUED) {
                        return {
                            ...milestone,
                            username: FMH_PROGRAM_SUPPORT,
                            comment: maskNonStandardReasons(milestone.comment, DiscontinueReasons),
                        };
                    }
                    if (
                        milestone.milestone_type ===
                        ReferralTimeline.MilestoneTypes.REFERRAL_UNMANAGED
                    ) {
                        return {
                            ...milestone,
                            username: FMH_PROGRAM_SUPPORT,
                            comment: maskNonStandardReasons(milestone.comment, UnmanageReasons),
                            additional_data: {
                                ...milestone.additional_data,
                                disposition: undefined,
                                reject_code: undefined,
                                outcome: undefined,
                            },
                        };
                    }
                    if (
                        milestone.milestone_type ===
                        ReferralTimeline.MilestoneTypes.REFERRAL_SERVICEABLE
                    ) {
                        return {
                            ...milestone,
                            additional_data: {
                                ...milestone.additional_data,
                                disposition: undefined,
                                reject_code: undefined,
                                outcome: undefined,
                            },
                        };
                    }
                    if (
                        milestone.milestone_type ===
                        ReferralTimeline.MilestoneTypes.UPDATE_PRODUCT_TYPE
                    ) {
                        return {
                            ...milestone,
                            username: FMH_PROGRAM_SUPPORT,
                            comment: maskNonStandardReasons(
                                milestone.comment,
                                UpdateProductTypeReasons,
                            ),
                        };
                    }
                    if (milestone.milestone_type === ReferralTimeline.MilestoneTypes.TRANSITION) {
                        return {
                            ...milestone,
                            username: FMH_PROGRAM_SUPPORT,
                            title:
                                supplierMap && milestone.additional_data
                                    ? milestone.title.replace(
                                          milestone.additional_data.new_supplier_npi,
                                          supplierMap[milestone.additional_data?.new_supplier_npi]
                                              ?.name ?? '',
                                      )
                                    : milestone.title,
                            comment: maskNonStandardReasons(
                                milestone.comment,
                                UpdateSupplierReasons,
                            ),
                        };
                    }
                    if (
                        milestone.milestone_type ===
                        ReferralTimeline.MilestoneTypes.TREATMENT_SEGMENT_SWITCHED
                    ) {
                        return {
                            ...milestone,
                            additional_data: {
                                ...milestone.additional_data,
                                new_segment_name:
                                    segments?.segmentsMap?.[
                                        milestone.additional_data?.new_segment_id
                                    ],
                            },
                        };
                    }
                    return milestone;
                })
                .filter((milestone) => milestone !== undefined)
                // Remove NO BID milestone if there is a POST LIVE REFERRAL ASSIGNMENT milestone
                .filter(
                    (milestone, _, milestones) =>
                        milestone.title !== 'Live Referral Had No Bids' ||
                        !milestones.some(
                            (m) =>
                                m?.milestone_type ===
                                ReferralTimeline.MilestoneTypes.POST_LIVE_REFERRAL_ASSIGNMENT,
                        ),
                )
                // Remove POST LIVE REFERRAL ASSIGNMENT milestone
                .filter(
                    (milestone) =>
                        milestone.milestone_type !==
                        ReferralTimeline.MilestoneTypes.POST_LIVE_REFERRAL_ASSIGNMENT,
                ),
        [referralMilestones, segments?.segmentsMap, showOpportunityDetailsInPayersApp, supplierMap],
    );
    return { enrichedMilestones, referralMilestonesFetching };
};

/** Returns the name of the segment a referral was transferred to if milestone is present */
export const useTransferredSegmentName = (referralId: string) => {
    const { enrichedMilestones } = useEnrichedReferralMilestones(referralId);
    return enrichedMilestones?.find(
        (milestone) =>
            milestone.milestone_type === ReferralTimeline.MilestoneTypes.TREATMENT_SEGMENT_SWITCHED,
    )?.additional_data?.new_segment_name;
};
